import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import OnboardingChecklist from '../../compnents/OnboardingChecklist';
import WorkspaceProfilePrompt from '../../compnents/WorkspaceProfilePrompt';
import {
    Badge,
    Box,
    Button,
    Container,
    Divider,
    Grid,
    Heading,
    HStack,
    Icon,
    Progress,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Table,
    Tabs,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
} from '@chakra-ui/react';
import {
    FiActivity,
    FiBarChart2,
    FiBriefcase,
    FiCheckCircle,
    FiClock,
    FiClipboard,
    FiDownload,
    FiFileText,
    FiGlobe,
    FiKey,
    FiPhone,
    FiRefreshCw,
    FiShield,
    FiTrendingUp,
    FiUploadCloud,
    FiUsers,
} from 'react-icons/fi';
import { demoTrialSampleAssetPath, demoTrialSampleCount, demoTrialSampleFileName, demoDatasetSummary, demoSpotlightCustomers } from '../../content/demoDataset';
import DashboardRoleBrief from '../../compnents/DashboardRoleBrief';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const demoStats = [
    {
        label: 'Customers in demo',
        value: demoDatasetSummary.customersCount.toLocaleString(),
        detail: demoDatasetSummary.customersLabel,
        icon: FiUsers,
        color: 'blue',
    },
    {
        label: 'Behavioral fields',
        value: demoDatasetSummary.columnsCount,
        detail: '9 required + 19 enhanced fields',
        icon: FiActivity,
        color: 'teal',
    },
    {
        label: 'Average risk',
        value: demoDatasetSummary.avgRiskProfile,
        detail: 'Risk profile distribution',
        icon: FiTrendingUp,
        color: 'orange',
    },
    {
        label: 'Knowledge score',
        value: demoDatasetSummary.avgKnowledgeScore,
        detail: 'Financial knowledge signal',
        icon: FiBarChart2,
        color: 'purple',
    },
];

const biasBenchmarks = [
    { label: 'Home bias', value: 72, color: 'orange' },
    { label: 'Social hype sensitivity', value: 64, color: 'purple' },
    { label: 'Loss aversion', value: 58, color: 'red' },
    { label: 'Under-diversification', value: 49, color: 'teal' },
];

const businessFeatures = [
    { icon: FiGlobe, title: 'Personalized domain', detail: 'Tenant-specific branded portal for client teams.' },
    { icon: FiShield, title: 'Audit and compliance views', detail: 'Operational controls for larger advisory organizations.' },
    { icon: FiKey, title: 'Business developer portal', detail: 'API keys are gated to Business admins and scoped before production use.' },
];

// Four rows of the synthetic extract, matching demoSpotlightCustomers.
// Each reason is something to verify in the data, never a prediction about
// the client and never a statement about an outcome.
const demoActionQueue = [
    {
        customerId: 'CUST3490',
        client: 'Conservative profile, concentrated portfolio',
        owner: 'Adviser: Alvaro',
        priority: 9.4,
        urgency: 'High',
        risk: '3/10',
        reason: 'Declared risk is conservative while 42% sits in a single stock.',
        nextAction: 'Confirm the mandate on file, then review the concentration against it.',
        color: 'red',
    },
    {
        customerId: 'CUST3089',
        client: 'High declared risk, low knowledge score',
        owner: 'Adviser: Alvaro',
        priority: 9.1,
        urgency: 'High',
        risk: '9/10',
        reason: 'Declared risk 9/10 with a knowledge score of 21/100 and recorded leverage.',
        nextAction: 'Record the rationale for the current positioning before the next instruction.',
        color: 'orange',
    },
    {
        customerId: 'CUST3511',
        client: 'Drift with no source date',
        owner: 'Adviser: Clara',
        priority: 8.7,
        urgency: 'High',
        risk: '2/10',
        reason: 'Drift is present and the extract carried no observation date for this client.',
        nextAction: 'Confirm the source date before treating any signal as current.',
        color: 'teal',
    },
    {
        customerId: 'CUST2973',
        client: 'Cash position with no mandate',
        owner: 'Adviser: Clara',
        priority: 8.2,
        urgency: 'High',
        risk: '4/10',
        reason: '38% in cash and no reference model to compare it to.',
        nextAction: 'Ask for the mandate, then reassess.',
        color: 'purple',
    },
];

const roleViews = [
    {
        key: 'admin',
        label: 'Admin',
        icon: FiBriefcase,
        color: 'blue',
        title: 'Workspace control and buyer impact',
        description: 'Admin users see rollout readiness, RM coverage, client activation, exports, and plan controls.',
        metrics: [
            { label: 'Dataset readiness', value: 'Ready', help: 'Synthetic demo data loaded' },
            { label: 'RM coverage', value: '82%', help: 'Customers assigned to an RM' },
            { label: 'Open actions', value: '24', help: 'Client follow-ups awaiting owner' },
        ],
        surfaces: ['Platform Impact', 'Customer Base Insights', 'Individual Scores', 'Bias Benchmark', 'Reports and exports'],
    },
    {
        key: 'rm',
        label: 'RM',
        icon: FiPhone,
        color: 'teal',
        title: 'Daily relationship manager workflow',
        description: 'RMs need a prioritized book, reasons to call, and a simple way to mark progress after every client action.',
        metrics: [
            { label: 'Calls due today', value: '8', help: 'Highest priority customers' },
            { label: 'Avg priority', value: '7.8', help: 'Who-to-call queue average' },
            { label: 'Tracked this week', value: '16', help: 'Actions logged by RMs' },
        ],
        surfaces: ['Who To Call', 'Own Book', 'Priority Scores', 'Risk Ranking', 'Segments'],
    },
    {
        key: 'compliance',
        label: 'Compliance',
        icon: FiShield,
        color: 'orange',
        title: 'Suitability and audit evidence',
        description: 'Compliance needs review queues, audit trail context, and behavioral evidence before files are marked complete.',
        metrics: [
            { label: 'Files for review', value: '11', help: 'Synthetic suitability review queue' },
            { label: 'High-risk flags', value: '7', help: 'Behavioral risk requires evidence' },
            { label: 'Audit coverage', value: '93%', help: 'Actions with traceable rationale' },
        ],
        surfaces: ['Compliance Dashboard', 'Individual Scores', 'Bias Benchmark', 'Audit Trail', 'Evidence export'],
    },
    {
        key: 'head',
        label: 'Head',
        icon: FiTrendingUp,
        color: 'pink',
        title: 'Advisory leadership dashboard',
        description: 'Heads of Advisory need team coverage, trend direction, RM execution quality, and client-base risk signals.',
        metrics: [
            { label: 'RM execution', value: '76%', help: 'Actions completed or scheduled' },
            { label: 'At-risk clients', value: '42', help: 'Churn and suitability proxy' },
            { label: 'Prep time saved', value: '31%', help: 'Demo operating target' },
        ],
        surfaces: ['Head Dashboard', 'Team performance', 'Behavioral trends', 'RM coverage', 'Bias Benchmark'],
    },
    {
        key: 'portfolio',
        label: 'Portfolio Adviser',
        icon: FiBarChart2,
        color: 'purple',
        title: 'Portfolio fit and client strategy',
        description: 'Portfolio advisers need to connect behavioral profile, risk fit, concentration, and next recommendation context.',
        metrics: [
            { label: 'Mismatch cases', value: '18', help: 'Profile vs portfolio signals' },
            { label: 'Concentration flags', value: '29', help: 'Home-bias and under-diversification' },
            { label: 'Review candidates', value: '14', help: 'Ready for adviser follow-up' },
        ],
        surfaces: ['Portfolio Dashboard', 'Customer Insights', 'Portfolio Fit', 'Scenario notes', 'Bias Benchmark'],
    },
];

const playgroundRoleDemoDashboards = [
    {
        key: 'rm-dashboard',
        label: 'RM Dashboard',
        roleLabel: 'Relationship Manager',
        color: 'teal',
        title: 'RM demo preview while setup completes',
        description: 'Playground role demo view: a relationship manager can see book health, priority clients, and next actions before a firm uploads its own data.',
        highlights: [
            { label: 'Sample clients', value: '42', help: 'Synthetic RM book for demo context' },
            { label: 'Priority actions', value: '9', help: 'Calls and reviews to stage' },
            { label: 'Meeting prep', value: '18', help: 'Clients needing advisory follow-up' },
        ],
        focusAreas: ['RM Dashboard', 'Own Book', 'Who To Call', 'Priority Scores', 'Risk Ranking', 'Segments'],
        actions: [
            { title: 'Call top priority client', detail: 'Use the who-to-call list to start with high-impact relationships.', badge: 'RM' },
            { title: 'Review risk movement', detail: 'Open the risk ranking before the next advisory conversation.', badge: 'Risk' },
            { title: 'Prepare next meeting', detail: 'Translate signals into a short client-facing agenda.', badge: 'Next best action' },
        ],
    },
    {
        key: 'compliance-dashboard',
        label: 'Compliance',
        roleLabel: 'Compliance Officer',
        color: 'orange',
        title: 'Compliance demo preview while setup completes',
        description: 'Playground governance demo: oversight queues, benchmark drift, and audit-ready notes are visible without requiring live tenant credentials.',
        highlights: [
            { label: 'Outlier checks', value: '12', help: 'Synthetic review queue' },
            { label: 'Benchmark drift', value: '3', help: 'Signals to investigate' },
            { label: 'Audit notes', value: 'Ready', help: 'Governance pack placeholder' },
        ],
        focusAreas: ['Compliance Dashboard', 'Individual Scores', 'Bias Benchmark', 'Audit-ready notes'],
        actions: [
            { title: 'Review outlier queue', detail: 'Start from accounts that need supervisory attention.', badge: 'Control' },
            { title: 'Check benchmark drift', detail: 'Compare portfolio behavior against the expected baseline.', badge: 'Benchmark' },
            { title: 'Prepare audit note', detail: 'Capture the reasoning behind the compliance review.', badge: 'Evidence' },
        ],
    },
    {
        key: 'head-dashboard',
        label: 'Head',
        roleLabel: 'Head of Advisory',
        color: 'pink',
        title: 'Leadership demo preview while setup completes',
        description: 'Playground advisory leadership demo: top-line portfolio signals, coverage gaps, and steering priorities are shown as a buyer-facing demo.',
        highlights: [
            { label: 'Portfolio signals', value: '6', help: 'Synthetic steering indicators' },
            { label: 'Coverage gaps', value: '4', help: 'Teams needing attention' },
            { label: 'Executive readout', value: '1', help: 'Board-ready snapshot' },
        ],
        focusAreas: ['Head Dashboard', 'Team coverage', 'Portfolio benchmark', 'Risk concentration', 'Steering priorities'],
        actions: [
            { title: 'Scan portfolio risk', detail: 'Start from the highest-level trend before drilling down.', badge: 'Portfolio' },
            { title: 'Compare team coverage', detail: 'Identify where RM ownership or follow-up cadence needs work.', badge: 'Coverage' },
            { title: 'Open steering notes', detail: 'Turn insight into the next management conversation.', badge: 'Leadership' },
        ],
    },
    {
        key: 'portfolio-adviser-dashboard',
        label: 'Portfolio Adviser',
        roleLabel: 'Portfolio Adviser',
        color: 'purple',
        title: 'Portfolio adviser demo preview while setup completes',
        description: 'Playground portfolio advisory demo: portfolio-level signals, customer insight, and recommendation context are shown together.',
        highlights: [
            { label: 'Recommendation themes', value: '7', help: 'Synthetic advisory angles' },
            { label: 'Client reviews', value: '14', help: 'Accounts to inspect' },
            { label: 'Suitability checks', value: '5', help: 'Items to validate' },
        ],
        focusAreas: ['Portfolio Dashboard', 'Customer Insights', 'Recommendation context', 'Benchmark checks'],
        actions: [
            { title: 'Review recommendations', detail: 'Start from portfolio themes that can become client actions.', badge: 'Advisory' },
            { title: 'Compare suitability', detail: 'Validate whether the suggested direction fits the client context.', badge: 'Suitability' },
            { title: 'Build advisory plan', detail: 'Convert portfolio signals into a clear client strategy.', badge: 'Plan' },
        ],
    },
    {
        key: 'read-only-benchmark',
        label: 'View Only',
        roleLabel: 'Read-only Stakeholder',
        color: 'blue',
        title: 'Read-only demo preview while setup completes',
        description: 'Playground read-only demo: benchmark views stay readable and presentation-friendly without operational controls.',
        highlights: [
            { label: 'Benchmark views', value: '3', help: 'Synthetic snapshots' },
            { label: 'Controls', value: 'Read only', help: 'No operational edits' },
            { label: 'Share state', value: 'Ready', help: 'Stakeholder-friendly view' },
        ],
        focusAreas: ['Bias Benchmark', 'Presentation view', 'Safe sharing'],
        actions: [
            { title: 'Read benchmark summary', detail: 'Review the top-level signal without editing customer data.', badge: 'Read only' },
            { title: 'Check share context', detail: 'Keep the view suitable for passive stakeholders.', badge: 'Controlled' },
            { title: 'Flag follow-up', detail: 'Capture questions for the admin or compliance owner.', badge: 'Follow-up' },
        ],
    },
];

function DashboardFree() {
    const navigate = useNavigate();
    const { user, token } = useContext(AuthContext);
    const [demoActions, setDemoActions] = useState({});

    // Funnel marker: record that the demo workspace was opened (server-side,
    // idempotent per tenant; failures are silent by design).
    useEffect(() => {
        if (!token) return;
        axios.post(`${API_BASE_URL}/content/analytics/demo-opened`, {}, {
            headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
    }, [token]);

    // Real onboarding context from known workspace state. A logged-in user has
    // a verified account; uploads come from the user record.
    const onboardingCtx = {
        verified: true,
        subscription: user?.subscription,
        uploadsCount: user?.noOfFileUploads || 0,
    };

    const openTrial = () => {
        navigate('/subscription?trial=pro');
    };

    const updateDemoAction = (customerId, status) => {
        setDemoActions((prev) => ({
            ...prev,
            [customerId]: status,
        }));
    };

    const completedActions = Object.values(demoActions).filter((status) => status === 'completed').length;
    const scheduledActions = Object.values(demoActions).filter((status) => status === 'scheduled').length;

    return (
        <Container maxW="container.xl" py={8} px={{ base: 4, md: 6 }}>
            <VStack align="stretch" spacing={6}>
                {/* Registration asks for two fields; this is where the rest
                    gets asked, on the first screen a new workspace opens. */}
                <WorkspaceProfilePrompt />
                <OnboardingChecklist ctx={onboardingCtx} />
                <Box
                    bg="white"
                    borderWidth="1px"
                    borderColor="brand.divider"
                    borderRadius="lg"
                    p={{ base: 5, md: 7 }}
                    boxShadow="sm"
                >
                    <Grid templateColumns={{ base: '1fr', xl: '1.35fr 0.8fr' }} gap={6} alignItems="center">
                        <VStack align="start" spacing={4}>
                            <Badge colorScheme="teal" borderRadius="full" px={3} py={1}>
                                Demo workspace unlocked
                            </Badge>
                            <Box>
                                <Heading size="lg" color="gray.800" mb={3}>
                                    Explore the full B2B story before uploading client data.
                                </Heading>
                                <Text color="gray.600" maxW="3xl">
                                    This synthetic workspace shows five role views, client actions, scoring, bias signals, RM workflow,
                                    and Business-plan surfaces without requiring a paid checkout or a real customer dataset.
                                </Text>
                            </Box>
                            <HStack spacing={3} flexWrap="wrap">
                                <Button colorScheme="blue" leftIcon={<FiCheckCircle />} onClick={openTrial}>
                                    Start a 30-day trial
                                </Button>
                                <Button variant="outline" colorScheme="blue" leftIcon={<FiUploadCloud />} onClick={() => navigate('/datasets')}>
                                    Upload your dataset
                                </Button>
                                <Button
                                    as="a"
                                    href={demoTrialSampleAssetPath}
                                    download={demoTrialSampleFileName}
                                    variant="ghost"
                                    colorScheme="teal"
                                    leftIcon={<FiDownload />}
                                >
                                    Import sample ({demoTrialSampleCount} clients)
                                </Button>
                            </HStack>
                        </VStack>

                        <Box bg="brand.tealSoft" borderRadius="lg" borderWidth="1px" borderColor="brand.divider" p={5}>
                            <Text fontSize="xs" fontWeight="bold" color="brand.navy2" textTransform="uppercase" mb={3}>
                                Full illustrative workspace
                            </Text>
                            <Text fontSize="sm" color="gray.600" mb={3}>
                                These workspace examples are separate from the {demoTrialSampleCount}-client import sample.
                            </Text>
                            <VStack align="stretch" spacing={3}>
                                <HStack justify="space-between">
                                    <Text fontSize="sm" color="gray.600">Coverage</Text>
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                        {demoDatasetSummary.topCountries.join(', ')}
                                    </Text>
                                </HStack>
                                <HStack justify="space-between">
                                    <Text fontSize="sm" color="gray.600">Average age</Text>
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">{demoDatasetSummary.avgAge}</Text>
                                </HStack>
                                <HStack justify="space-between">
                                    <Text fontSize="sm" color="gray.600">Plan access</Text>
                                    <Badge colorScheme="green">Demo unlocked</Badge>
                                </HStack>
                            </VStack>
                        </Box>
                    </Grid>
                </Box>

                <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
                    {demoStats.map((stat) => (
                        <Box key={stat.label} bg="white" borderRadius="lg" borderWidth="1px" borderColor={`${stat.color}.100`} p={5}>
                            <HStack justify="space-between" align="start" mb={4}>
                                <Box>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                                        {stat.label}
                                    </Text>
                                    <Text fontSize="2xl" fontWeight="bold" color={`${stat.color}.600`}>
                                        {stat.value}
                                    </Text>
                                </Box>
                                <Box bg={`${stat.color}.50`} borderRadius="md" p={2}>
                                    <Icon as={stat.icon} color={`${stat.color}.500`} boxSize={5} />
                                </Box>
                            </HStack>
                            <Text fontSize="sm" color="gray.600">{stat.detail}</Text>
                        </Box>
                    ))}
                </SimpleGrid>

                <Box bg="white" borderRadius="lg" borderWidth="1px" borderColor="teal.100" boxShadow="sm" p={{ base: 4, md: 6 }}>
                    <Grid templateColumns={{ base: '1fr', xl: '0.8fr 1.2fr' }} gap={6} alignItems="start">
                        <Box>
                            <Badge colorScheme="teal" borderRadius="full" px={3} py={1} mb={3}>
                                Client action queue
                            </Badge>
                            <Heading size="md" color="gray.800" mb={3}>
                                Turn client signals into next actions.
                            </Heading>
                            <Text color="gray.600" fontSize="sm" mb={5}>
                                This is the operational CTA layer buyers expect to see: who needs attention, why, who owns it,
                                and what should happen next.
                            </Text>
                            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3}>
                                <Box bg="teal.50" borderRadius="lg" borderWidth="1px" borderColor="teal.100" p={4}>
                                    <Stat>
                                        <StatLabel color="gray.600">Queue</StatLabel>
                                        <StatNumber color="teal.600">{demoActionQueue.length}</StatNumber>
                                    </Stat>
                                </Box>
                                <Box bg="green.50" borderRadius="lg" borderWidth="1px" borderColor="green.100" p={4}>
                                    <Stat>
                                        <StatLabel color="gray.600">Completed</StatLabel>
                                        <StatNumber color="green.600">{completedActions}</StatNumber>
                                    </Stat>
                                </Box>
                                <Box bg="brand.tealSoft" borderRadius="lg" borderWidth="1px" borderColor="brand.divider" p={4}>
                                    <Stat>
                                        <StatLabel color="gray.600">Scheduled</StatLabel>
                                        <StatNumber color="brand.navy">{scheduledActions}</StatNumber>
                                    </Stat>
                                </Box>
                            </SimpleGrid>
                        </Box>

                        <VStack align="stretch" spacing={3}>
                            {demoActionQueue.map((item) => {
                                const status = demoActions[item.customerId] || 'ready';
                                const statusColor = status === 'completed' ? 'green' : status === 'scheduled' ? 'blue' : status === 'snoozed' ? 'orange' : 'gray';

                                return (
                                    <Box
                                        key={item.customerId}
                                        borderWidth="1px"
                                        borderColor={`${item.color}.100`}
                                        bg={`${item.color}.50`}
                                        borderRadius="lg"
                                        p={4}
                                    >
                                        <Grid templateColumns={{ base: '1fr', lg: '1fr auto' }} gap={4} alignItems="start">
                                            <Box>
                                                <HStack spacing={2} flexWrap="wrap" mb={2}>
                                                    <Text fontWeight="bold" color="gray.800">{item.customerId}</Text>
                                                    <Badge colorScheme={item.color}>{item.urgency}</Badge>
                                                    <Badge colorScheme="gray">{item.owner}</Badge>
                                                    <Badge colorScheme={statusColor}>{status}</Badge>
                                                </HStack>
                                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">{item.client}</Text>
                                                <Text fontSize="sm" color="gray.600" mt={1}>{item.reason}</Text>
                                                <HStack spacing={3} flexWrap="wrap" mt={3}>
                                                    <Badge colorScheme="purple">Priority {item.priority}/10</Badge>
                                                    <Badge colorScheme="red">Risk {item.risk}</Badge>
                                                    <Text fontSize="sm" color="gray.700">
                                                        <b>Next:</b> {item.nextAction}
                                                    </Text>
                                                </HStack>
                                            </Box>
                                            <HStack spacing={2} flexWrap="wrap" justify={{ base: 'flex-start', lg: 'flex-end' }}>
                                                <Button size="sm" colorScheme="blue" variant="outline" leftIcon={<FiPhone />} onClick={() => updateDemoAction(item.customerId, 'scheduled')}>
                                                    Schedule
                                                </Button>
                                                <Button size="sm" colorScheme="green" leftIcon={<FiCheckCircle />} onClick={() => updateDemoAction(item.customerId, 'completed')}>
                                                    Complete
                                                </Button>
                                                <Button size="sm" colorScheme="orange" variant="outline" leftIcon={<FiClock />} onClick={() => updateDemoAction(item.customerId, 'snoozed')}>
                                                    Snooze
                                                </Button>
                                            </HStack>
                                        </Grid>
                                    </Box>
                                );
                            })}
                        </VStack>
                    </Grid>
                </Box>

                <Box bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.200" boxShadow="sm">
                    <Tabs colorScheme="blue" variant="enclosed">
                        <TabList overflowX="auto" overflowY="hidden" flexWrap="nowrap">
                            {roleViews.map((role) => (
                                <Tab key={role.key} whiteSpace="nowrap">
                                    <HStack spacing={2}>
                                        <Icon as={role.icon} />
                                        <Text>{role.label}</Text>
                                    </HStack>
                                </Tab>
                            ))}
                        </TabList>

                        <TabPanels>
                            {roleViews.map((role) => (
                                <TabPanel key={role.key} p={{ base: 4, md: 6 }}>
                                    <VStack align="stretch" spacing={6}>
                                        <Grid templateColumns={{ base: '1fr', xl: '0.85fr 1.15fr' }} gap={6}>
                                            <Box bg={`${role.color}.50`} borderRadius="lg" borderWidth="1px" borderColor={`${role.color}.100`} p={5}>
                                                <HStack spacing={3} mb={3}>
                                                    <Box bg="white" borderRadius="md" p={2}>
                                                        <Icon as={role.icon} color={`${role.color}.600`} boxSize={5} />
                                                    </Box>
                                                    <Badge colorScheme={role.color}>{role.label}</Badge>
                                                </HStack>
                                                <Heading size="md" color="gray.800" mb={3}>{role.title}</Heading>
                                                <Text fontSize="sm" color="gray.600">{role.description}</Text>
                                                <Divider my={5} />
                                                <VStack align="stretch" spacing={3}>
                                                    {role.surfaces.map((surface) => (
                                                        <HStack key={surface} align="start">
                                                            <Icon as={FiCheckCircle} color={`${role.color}.500`} mt={0.5} />
                                                            <Text fontSize="sm" color="gray.700">{surface}</Text>
                                                        </HStack>
                                                    ))}
                                                </VStack>
                                            </Box>

                                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                                {role.metrics.map((metric) => (
                                                    <Box key={metric.label} bg="gray.50" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={5}>
                                                        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={2}>
                                                            {metric.label}
                                                        </Text>
                                                        <Text fontSize="2xl" fontWeight="bold" color={`${role.color}.600`}>
                                                            {metric.value}
                                                        </Text>
                                                        <Text fontSize="sm" color="gray.600" mt={2}>{metric.help}</Text>
                                                    </Box>
                                                ))}
                                            </SimpleGrid>
                                        </Grid>

                                        <Grid templateColumns={{ base: '1fr', xl: '1fr 1fr' }} gap={6}>
                                            <Box>
                                                <Heading size="sm" color="gray.800" mb={4}>
                                                    Bias benchmark preview
                                                </Heading>
                                                <VStack align="stretch" spacing={4}>
                                                    {biasBenchmarks.map((item) => (
                                                        <Box key={item.label}>
                                                            <HStack justify="space-between" mb={2}>
                                                                <Text fontSize="sm" color="gray.700">{item.label}</Text>
                                                                <Badge colorScheme={item.color}>{item.value}/100</Badge>
                                                            </HStack>
                                                            <Progress value={item.value} colorScheme={item.color} borderRadius="full" size="sm" />
                                                        </Box>
                                                    ))}
                                                </VStack>
                                            </Box>

                                            <Box overflowX="auto">
                                                <Heading size="sm" color="gray.800" mb={4}>
                                                    Spotlight customers
                                                </Heading>
                                                <Table size="sm" variant="simple">
                                                    <Thead bg="brand.tealSoft">
                                                        <Tr>
                                                            <Th>Customer</Th>
                                                            <Th>Signal</Th>
                                                            <Th>Metrics</Th>
                                                        </Tr>
                                                    </Thead>
                                                    <Tbody>
                                                        {demoSpotlightCustomers.slice(0, 4).map((customer) => (
                                                            <Tr key={`${role.key}-${customer.customerId}`}>
                                                                <Td>
                                                                    <Text fontWeight="semibold" color="gray.800">{customer.customerId}</Text>
                                                                    <Badge colorScheme="blue">{customer.country}</Badge>
                                                                </Td>
                                                                <Td maxW="260px">
                                                                    <Text fontSize="sm" color="gray.700">{customer.angle}</Text>
                                                                </Td>
                                                                <Td>
                                                                    <VStack align="start" spacing={1}>
                                                                        {customer.metrics.slice(0, 2).map((metric) => (
                                                                            <Badge key={metric} colorScheme="gray" borderRadius="full">
                                                                                {metric}
                                                                            </Badge>
                                                                        ))}
                                                                    </VStack>
                                                                </Td>
                                                            </Tr>
                                                        ))}
                                                    </Tbody>
                                                </Table>
                                            </Box>
                                        </Grid>
                                    </VStack>
                                </TabPanel>
                            ))}
                        </TabPanels>
                    </Tabs>
                </Box>

                <Box bg="white" borderRadius="lg" borderWidth="1px" borderColor="brand.divider" boxShadow="sm" p={{ base: 4, md: 6 }}>
                    <VStack align="stretch" spacing={5}>
                        <Box>
                            <Badge colorScheme="blue" borderRadius="full" px={3} py={1} mb={3}>
                                Playground role dashboard demos
                            </Badge>
                            <Heading size="md" color="gray.800" mb={2}>
                                Demo versions of the tenant role dashboards are available inside Playground.
                            </Heading>
                            <Text fontSize="sm" color="gray.600" maxW="3xl">
                                These tabs show the role-specific Business story inside Playground without calling retired tenant demo APIs:
                                RM, Compliance, Head of Advisory, Portfolio Adviser, and read-only stakeholder views.
                            </Text>
                        </Box>

                        <Tabs colorScheme="blue" variant="soft-rounded">
                            <TabList overflowX="auto" overflowY="hidden" flexWrap="nowrap">
                                {playgroundRoleDemoDashboards.map((dashboard) => (
                                    <Tab key={dashboard.key} whiteSpace="nowrap">
                                        {dashboard.label}
                                    </Tab>
                                ))}
                            </TabList>

                            <TabPanels>
                                {playgroundRoleDemoDashboards.map((dashboard) => (
                                    <TabPanel key={dashboard.key} px={0} pb={0}>
                                        <VStack align="stretch" spacing={4}>
                                            <DashboardRoleBrief
                                                title={dashboard.title}
                                                description={dashboard.description}
                                                highlights={dashboard.highlights}
                                                focusAreas={dashboard.focusAreas}
                                                lastUpdatedLabel="Playground synthetic demo"
                                                bg={`${dashboard.color}.50`}
                                                borderColor={`${dashboard.color}.100`}
                                                titleColor="gray.800"
                                                descriptionColor="gray.600"
                                                accentColor={`${dashboard.color}.600`}
                                                cardBg="white"
                                            />

                                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                                                {dashboard.actions.map((item) => (
                                                    <Box
                                                        key={item.title}
                                                        borderWidth="1px"
                                                        borderColor={`${dashboard.color}.100`}
                                                        borderRadius="lg"
                                                        bg="gray.50"
                                                        p={4}
                                                    >
                                                        <Badge colorScheme={dashboard.color} mb={2}>
                                                            {item.badge}
                                                        </Badge>
                                                        <Text fontSize="sm" fontWeight="bold" color="gray.800">
                                                            {item.title}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.600" mt={1}>
                                                            {item.detail}
                                                        </Text>
                                                    </Box>
                                                ))}
                                            </SimpleGrid>
                                        </VStack>
                                    </TabPanel>
                                ))}
                            </TabPanels>
                        </Tabs>
                    </VStack>
                </Box>

                <Box bg="white" borderRadius="lg" borderWidth="1px" borderColor="purple.100" boxShadow="sm" p={{ base: 4, md: 6 }}>
                    <Grid templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }} gap={4}>
                        {businessFeatures.map((feature) => (
                            <Box key={feature.title} borderWidth="1px" borderColor="purple.100" borderRadius="lg" p={5} bg="purple.50">
                                <HStack align="start" spacing={3} mb={3}>
                                    <Box bg="white" borderRadius="md" p={2}>
                                        <Icon as={feature.icon} color="purple.600" boxSize={5} />
                                    </Box>
                                    <Box>
                                        <Text fontWeight="semibold" color="gray.800">{feature.title}</Text>
                                        <Text mt={1} fontSize="sm" color="gray.600">{feature.detail}</Text>
                                    </Box>
                                </HStack>
                            </Box>
                        ))}
                    </Grid>
                    <HStack mt={5} spacing={3} flexWrap="wrap">
                        <Button colorScheme="blue" leftIcon={<FiClipboard />} onClick={openTrial}>
                            Start a 30-day trial
                        </Button>
                        <Button variant="outline" colorScheme="blue" leftIcon={<FiFileText />} onClick={() => navigate('/subscription')}>
                            Compare plans
                        </Button>
                        <Button variant="ghost" colorScheme="teal" leftIcon={<FiRefreshCw />} onClick={() => navigate('/datasets')}>
                            Process your own dataset
                        </Button>
                    </HStack>
                </Box>
            </VStack>
        </Container>
    );
}

export default DashboardFree;
